import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * LocatieTracker
 * ---------------
 * Verzendt regelmatig lat/lon naar POST /api/live-locatie/ping wanneer:
 *  - de gebruiker consent heeft gegeven (tracking_actief=true)
 *  - de PWA/browser tab open is
 *
 * Renderloos component. Plaats binnen <Layout> zodat het alleen actief is
 * wanneer een gebruiker ingelogd is. Bij tab-close stopt tracking automatisch.
 */
export default function LocatieTracker() {
  const { user } = useAuth();
  const [consent, setConsent] = useState(null); // {tracking_actief, interval_min}
  const timerRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastPingRef = useRef(0);

  const loadConsent = useCallback(async () => {
    try {
      const r = await api.get("/live-locatie/mij");
      setConsent(r.data);
    } catch (e) { setConsent({ tracking_actief: false }); }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadConsent();
  }, [user, loadConsent]);

  // Poll consent every 60s (user may have toggled it elsewhere)
  useEffect(() => {
    if (!user) return;
    const id = setInterval(loadConsent, 60000);
    return () => clearInterval(id);
  }, [user, loadConsent]);

  const sendPing = useCallback(async (pos, type = "ping") => {
    const now = Date.now();
    // Throttle: max once per 30s regardless
    if (type === "ping" && now - lastPingRef.current < 30000) return;
    lastPingRef.current = now;
    try {
      await api.post("/live-locatie/ping", {
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        speed: pos.coords.speed,
        type,
      });
    } catch (e) { /* silent */ }
  }, []);

  // Start / stop tracking based on consent
  useEffect(() => {
    if (!user || !consent?.tracking_actief) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (!navigator.geolocation) return;

    const interval = Math.max(1, Math.min(60, consent.interval_min || 5)) * 60 * 1000;

    // One immediate ping
    navigator.geolocation.getCurrentPosition(
      (pos) => sendPing(pos, "ping"),
      () => {},
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 10000 }
    );

    // Periodic pings
    timerRef.current = setInterval(() => {
      if (document.hidden) return; // don't ping while tab hidden
      navigator.geolocation.getCurrentPosition(
        (pos) => sendPing(pos, "ping"),
        () => {},
        { enableHighAccuracy: false, maximumAge: 60000, timeout: 10000 }
      );
    }, interval);

    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [user, consent, sendPing]);

  return null;
}
